import { useEffect, useMemo, useState } from 'react';
import { firstValueFrom } from 'rxjs';

import { CasinoService } from 'services/casino';
import { getCloakedMnums, subscribeCloakCache } from 'lib/cloakEgmsCache';

/** Slot Check catalog id — excluded from Cloak EGMs (matches CLOAK_EXCLUDED_CASINO_ID). */
export const SLOT_CHECK_CASINO_ID = 4;

/**
 * The casino's live cloaked EGM mnums, as a Set of strings ready to test against
 * `slot_number` (which is a string at runtime as often as a number — see
 * normalisation note in the EGM list code).
 *
 * Cloaking is deliberately not folded into the Explore list API, so every surface
 * that shows EGMs has to subtract this set itself. Sharing one hook keeps the
 * drawer list and the map's pins working off the same set — if they drift, pin
 * numbers stop matching list row numbers.
 *
 * Fails open: a cloak GET error yields an empty set, so a backend hiccup shows
 * everything rather than blanking Explore.
 */
export const useCloakedMnums = (casinoId: string | number | undefined): Set<string> => {
  const casinoSvc = useMemo(() => new CasinoService(), []);
  const [cloakedMnums, setCloakedMnums] = useState<string[]>([]);
  const [cloakTick, setCloakTick] = useState(0);

  useEffect(() => {
    return subscribeCloakCache((id) => {
      if (!id || String(id) === String(casinoId)) {
        setCloakTick((t) => t + 1);
      }
    });
  }, [casinoId]);

  useEffect(() => {
    const idNum = Number(casinoId);
    if (!Number.isInteger(idNum) || idNum <= 0 || idNum === SLOT_CHECK_CASINO_ID) {
      setCloakedMnums([]);
      return;
    }
    const id = String(idNum);
    let cancelled = false;
    getCloakedMnums(id, async () => {
      const res = await firstValueFrom(casinoSvc.GetCloakedEgms(id));
      const payload = res && typeof res === 'object' ? res : null;
      const mnums = payload && Array.isArray(payload.mnums) ? payload.mnums : [];
      return mnums.map((m) => String(m));
    })
      .then((mnums) => {
        if (!cancelled) setCloakedMnums(mnums);
      })
      .catch(() => {
        // Fail-open: show all Explore EGMs if the cloak GET errors.
        if (!cancelled) setCloakedMnums([]);
      });
    return () => {
      cancelled = true;
    };
  }, [casinoId, cloakTick, casinoSvc]);

  return useMemo(() => new Set(cloakedMnums.map((m) => String(m))), [cloakedMnums]);
};
